import React from 'react';
import { useLocation } from 'react-router-dom';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { typography } from '../../design-system/tokens';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.props.resetKey !== prevProps.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    console.error('[LockedIn] Render error:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-black text-white p-8 flex items-center justify-center">
          <Card variant="danger" padding="md" rounded="2xl" className="max-w-lg w-full">
            <h1 className={`${typography.heading} text-red-400 mb-2`}>
              Something went wrong
            </h1>
            <p className={`${typography.body} mb-4`}>
              Reload to continue. If this keeps happening, clear the site data for LockedIn and try again.
            </p>
            {process.env.NODE_ENV !== 'production' && (
              <pre className="text-xs text-gray-500 bg-black/50 p-3 rounded-xl overflow-auto max-h-40">
                {this.state.error?.message || String(this.state.error)}
              </pre>
            )}
            <Button
              type="button"
              fullWidth
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Clears a caught render error when the user navigates (e.g. browser Back). */
export function AppErrorBoundary({ children }) {
  const location = useLocation();
  const resetKey = `${location.pathname}${location.search}${location.key}`;
  return <ErrorBoundary resetKey={resetKey}>{children}</ErrorBoundary>;
}
